<script lang="ts">
	import type { Mediator, SvelteAsr } from 'types/common'

	import { ChartConfiguration } from '@isoftdata/svelte-dashboard'
	import { hasPermission } from 'utility/permission'
	import { getContext, onMount, type ComponentProps } from 'svelte'
	import { graphql } from '$houdini'

	interface Props {
		asr: SvelteAsr
		charts: ComponentProps<typeof ChartConfiguration>['charts']
		defaultParameters: ComponentProps<typeof ChartConfiguration>['defaultParameters']
		reports: ComponentProps<typeof ChartConfiguration>['reports']
		selectedChartId?: number | null
	}

	let {
		//
		asr,
		charts,
		defaultParameters,
		reports,
		selectedChartId,
	}: Props = $props()

	let selectedChart: ComponentProps<typeof ChartConfiguration>['selectedChart'] = $state(null)

	const mediator = getContext<Mediator>('mediator')

	const createChartMutation = graphql(`
		mutation CreateDashboardChart($input: NewDashboardChartInput!) {
			createDashboardChart(input: $input) {
				id
			}
		}
	`)

	const updateChartMutation = graphql(`
		mutation UpdateDashboardChart($input: UpdateDashboardChartInput!) {
			updateDashboardChart(input: $input) {
				id
			}
		}
	`)

	const loadArbitraryChartData = graphql(`
		query LoadArbitraryChartData($input: LoadArbitraryChartDataInput!) {
			loadArbitraryChartData(input: $input)
		}
	`)

	onMount(() => {
		if (selectedChartId) {
			selectedChart = charts.find(c => c.chartId === selectedChartId) ?? null
		}
	})
</script>

<ChartConfiguration
	{charts}
	{reports}
	{defaultParameters}
	canEditQuery={hasPermission('DASHBOARD_CHART_EDIT_QUERY')}
	bind:selectedChart
	chartChange={({ chartId }) => asr.go(null, { selectedChartId: chartId ?? null }, { inherit: true })}
	confirm={() => asr.go(null, { lastSaveTime: Date.now() }, { inherit: true })}
	loadChartData={async (chart, parameters) => {
		const { data } = await loadArbitraryChartData.fetch({
			variables: {
				input: {
					chart: JSON.stringify(chart),
					parameterSelectionList: (parameters ?? []).map(parameter => {
						return {
							// here, we are leaving out `type` field from the parameter as our query does not take a `type` input field
							dates: {
								from: parameter.dates?.from ?? '',
								to: parameter.dates?.to ?? '',
							},
							name: parameter.name,
							value: parameter.value,
						}
					}),
				},
			},
		})

		return JSON.parse(data?.loadArbitraryChartData ?? '{}')
	}}
	showError={(heading, message) => {
		mediator.publish('showMessage', {
			heading,
			message,
			color: 'danger',
			time: false,
		})
	}}
	createChart={async chart => {
		const { data } = await createChartMutation.mutate({
			input: {
				dataSourceName: chart.dataSourceName,
				jsonString: chart.jsonString,
				name: chart.name,
				showChart: true,
				supertype: chart.supertype.toUpperCase() as 'EMBED' | 'GOOGLE' | 'TABLE',
				title: chart.title,
				description: '',
			},
		})
		return data?.createDashboardChart.id
	}}
	updateChart={async chart => {
		await updateChartMutation.mutate({
			input: {
				id: chart.id,
				jsonString: chart.jsonString,
				name: chart.name,
				showChart: true,
				title: chart.title,
				description: '',
			},
		})
	}}
></ChartConfiguration>
